import { CommonHelperFunctions } from "@kuika/kuika-cl-model";
import Axios from "axios";
import ObjectHash from "object-hash";

const backendUrl = CommonHelperFunctions.GetBackendUrl();
const LSP_URL = `${backendUrl}/languageserver`;

export interface LSPDiagnostic {
  severity: number;
  message: string;
  startLineNumber: number;
  startColumn: number;
  endLineNumber: number;
  endColumn: number;
  source: string;
}

export interface LSPCompletionItem {
  label: string;
  insertText: string;
  detail: string;
  kind: CompletionItemKind;
}

export interface HoverInfo {
  content: string;
  startLineNumber: number;
  startColumn: number;
  endLineNumber: number;
  endColumn: number;
}

export interface CodeAction {
  title: string;
  edit: string;
  editedCode: string;
  startLineNumber: number;
  startColumn: number;
  endLineNumber: number;
  endColumn: number;
}

export interface DefinitionInfo {
  startLineNumber: number;
  startColumn: number;
  endLineNumber: number;
  endColumn: number;
}

export enum CompletionItemKind {
  None = 0, // Value to use when no kind was provided
  Text = 1, // Text
  Method = 2, // Method
  Function = 3, // Function
  Constructor = 4, // Constructor
  Field = 5, // Field
  Variable = 6, // Variable
  Class = 7, // Class
  Interface = 8, // Interface
  Module = 9, // Module
  Property = 10, // Property
  Unit = 11, // Unit
  Value = 12, // Value
  Enum = 13, // Enum
  Keyword = 14, // Keyword
  Snippet = 15, // Snippet
  Color = 16, // Color
  File = 17, // File
  Reference = 18, // Reference
  Folder = 19, // Folder
  EnumMember = 20, // EnumMember
  Constant = 21, // Constant
  Struct = 22, // Struct
  Event = 23, // Event
  Operator = 24, // Operator
  TypeParameter = 25, // TypeParameter
  Macro = 118115, // Macro
  Namespace = 118116, // Namespace
  Template = 118117, // Template
  TypeDefinition = 118118, // TypeDefinition
  Union = 118119, // Union
  Delegate = 118120, // Delegate
  TagHelper = 118121, // TagHelper
  ExtensionMethod = 118122, // ExtensionMethod
  Element = 118123, // Element
  LocalResource = 118124, // LocalResource
  SystemResource = 118125, // SystemResource
  CloseElement = 118126 // CloseElement
}

export class LanguageServerService {
  private static compilerMessageCache: { [key: string]: string } = {};

  private static codeActionsCache: Map<string, { actions: CodeAction[]; timestamp: number }> = new Map();

  private static readonly CACHE_TTL = 5 * 60 * 1000; // 5 minutes

  private static readonly CODE_ACTIONS_CACHE_MAX = 50;

  // The key must include languageId + datasourceId: the same SQL text at the same position can
  // produce different code actions per dialect/datasource, so a sourceCode-only key returns stale,
  // wrong results once more than one language server shares this cache.
  private static getCacheKey(
    languageId: string,
    datasourceId: string | null,
    sourceCode: string,
    position?: number
  ): string {
    return `${languageId}_${datasourceId ?? ""}_${ObjectHash(sourceCode)}_${position ?? 0}`;
  }

  // Prune expired entries and cap total size (FIFO) so the cache cannot grow unbounded across a
  // long editing session.
  private static setCodeActionsCache(key: string, actions: CodeAction[]): void {
    const now = Date.now();
    for (const [k, v] of this.codeActionsCache) {
      if (now - v.timestamp >= this.CACHE_TTL) {
        this.codeActionsCache.delete(k);
      }
    }
    while (this.codeActionsCache.size >= this.CODE_ACTIONS_CACHE_MAX) {
      const oldest = this.codeActionsCache.keys().next().value;
      if (oldest === undefined) break;
      this.codeActionsCache.delete(oldest);
    }
    this.codeActionsCache.set(key, { actions, timestamp: now });
  }

  public static clearCodeActionsCache() {
    this.codeActionsCache.clear();
  }

  public static async GetDiagnostics(
    sourceCode: string,
    datasourceId: string | null,
    signal?: AbortSignal,
    languageId: string = "csharp"
  ): Promise<LSPDiagnostic[]> {
    try {
      const response = await Axios.post<LSPDiagnostic[]>(
        `${LSP_URL}/${languageId}/diagnostics`,
        {
          sourceCode,
          datasourceId
        },
        { signal }
      );
      return response.data;
    } catch (error: any) {
      return [];
    }
  }

  public static async GetCompilerMessage(errorCode: string): Promise<string> {
    try {
      // Check cache first
      if (this.compilerMessageCache[errorCode]) {
        return this.compilerMessageCache[errorCode];
      }

      const response = await Axios.get<string>(`${LSP_URL}/csharp/error/${errorCode}`);
      // Cache the result
      this.compilerMessageCache[errorCode] = response.data;
      return response.data;
    } catch (error: any) {
      return "";
    }
  }

  public static async GetCompletions(
    sourceCode: string,
    position: number,
    datasourceId: string | null,
    languageId: string = "csharp"
  ): Promise<LSPCompletionItem[]> {
    try {
      const response = await Axios.post<LSPCompletionItem[]>(`${LSP_URL}/${languageId}/completions`, {
        sourceCode,
        position,
        datasourceId
      });
      return response.data;
    } catch (error: any) {
      return [];
    }
  }

  public static async GetHoverInfo(
    sourceCode: string,
    position: number,
    datasourceId: string | null,
    languageId: string = "csharp"
  ): Promise<HoverInfo | null> {
    try {
      const response = await Axios.post<HoverInfo>(`${LSP_URL}/${languageId}/hover`, {
        sourceCode,
        position,
        datasourceId
      });
      return response.data;
    } catch (error: any) {
      return null;
    }
  }

  public static async GetCodeActions(
    sourceCode: string,
    datasourceId: string | null,
    position?: number,
    signal?: AbortSignal,
    languageId: string = "csharp"
  ): Promise<CodeAction[]> {
    try {
      const cacheKey = this.getCacheKey(languageId, datasourceId, sourceCode, position);
      const cached = this.codeActionsCache.get(cacheKey);

      if (cached && Date.now() - cached.timestamp < this.CACHE_TTL) {
        return cached.actions;
      }

      const response = await Axios.post<CodeAction[]>(
        `${LSP_URL}/${languageId}/codeactions`,
        {
          sourceCode,
          position,
          datasourceId
        },
        { signal }
      );

      this.setCodeActionsCache(cacheKey, response.data);

      return response.data;
    } catch (error: any) {
      return [];
    }
  }

  public static async GetDefinition(
    sourceCode: string,
    position: number,
    datasourceId: string | null,
    languageId: string = "csharp"
  ): Promise<DefinitionInfo | null> {
    try {
      const response = await Axios.post<DefinitionInfo>(`${LSP_URL}/${languageId}/definition`, {
        sourceCode,
        position,
        datasourceId
      });
      return response.data;
    } catch (error: any) {
      return null;
    }
  }

  public static async Format(sourceCode: string, languageId: string = "csharp"): Promise<string | null> {
    try {
      const response = await Axios.post<{ formattedCode: string }>(`${LSP_URL}/${languageId}/format`, {
        sourceCode
      });
      return response.data.formattedCode;
    } catch (error: any) {
      return null;
    }
  }

  public static async ApplyCodeAction(
    sourceCode: string,
    action: CodeAction,
    datasourceId: string | null,
    languageId: string = "csharp"
  ): Promise<string | null> {
    try {
      const response = await Axios.post<{ modifiedCode: string }>(`${LSP_URL}/${languageId}/codeaction/apply`, {
        sourceCode,
        action,
        datasourceId
      });
      return response.data.modifiedCode;
    } catch (error: any) {
      return null;
    }
  }

  public static clearCompilerMessageCache() {
    this.compilerMessageCache = {};
  }

  public static async ClearCache(languageId: string = "csharp"): Promise<void> {
    try {
      await Axios.post(`${LSP_URL}/${languageId}/clear-cache`);
    } catch (error: any) {
      console.error("Failed to clear language server cache:", error);
    }
  }
}
